import { useState } from "react";
import { ImportSourceStep } from "./ImportSourceStep";
import { ImportUploadStep } from "./ImportUploadStep";
import { ImportMappingStep } from "./ImportMappingStep";
import { ImportPreviewStep } from "./ImportPreviewStep";
import { ImportProgressStep } from "./ImportProgressStep";
import { ImportSummaryStep } from "./ImportSummaryStep";
import type { SourcePreset } from "./sourceMapping";

export type ImportStep = "source" | "upload" | "mapping" | "preview" | "importing" | "summary";

export interface ImportState {
    sourceKey: SourcePreset["key"] | null;
    file: File | null;
    csvHeaders: string[];
    csvRows: Record<string, string>[];
    columnMapping: Record<string, { mylesField: string; table: "contact" | "company" }>;
    importResult: {
        created: number;
        updated: number;
        duplicates: number;
        errors: number;
        conflictCount: number;
        importLogId?: number;
    } | null;
}

const initialState: ImportState = {
    sourceKey: null,
    file: null,
    csvHeaders: [],
    csvRows: [],
    columnMapping: {},
    importResult: null,
};

export function ImportWizard() {
    const [step, setStep] = useState<ImportStep>("source");
    const [state, setState] = useState<ImportState>(initialState);

    const updateState = (partial: Partial<ImportState>) =>
        setState((prev) => ({ ...prev, ...partial }));

    return (
        <div className="mx-auto max-w-4xl p-6">
            <h1 className="mb-6 text-2xl font-bold">Import Contacts</h1>
            {/* Step indicator */}
            <div className="mb-8 flex gap-2">
                {(["source", "upload", "mapping", "preview", "importing", "summary"] as const).map((s, i) => (
                    <div key={s} className={`flex items-center gap-2 ${step === s ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${step === s ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                            {i + 1}
                        </span>
                        <span className="hidden text-sm capitalize sm:inline">{s}</span>
                    </div>
                ))}
            </div>
            {step === "source" && (
                <ImportSourceStep onSelect={(key) => { updateState({ sourceKey: key }); setStep("upload"); }} />
            )}
            {step === "upload" && (
                <ImportUploadStep
                    sourceKey={state.sourceKey!}
                    onParsed={(headers, rows) => { updateState({ csvHeaders: headers, csvRows: rows }); setStep("mapping"); }}
                    onBack={() => setStep("source")}
                />
            )}
            {step === "mapping" && (
                <ImportMappingStep
                    csvHeaders={state.csvHeaders}
                    sourceKey={state.sourceKey!}
                    mapping={state.columnMapping}
                    onMappingChange={(mapping) => updateState({ columnMapping: mapping })}
                    onNext={() => setStep("preview")}
                    onBack={() => setStep("upload")}
                />
            )}
            {step === "preview" && (
                <ImportPreviewStep
                    rows={state.csvRows}
                    mapping={state.columnMapping}
                    onConfirm={() => setStep("importing")}
                    onBack={() => setStep("mapping")}
                />
            )}
            {step === "importing" && (
                <ImportProgressStep
                    rows={state.csvRows}
                    mapping={state.columnMapping}
                    sourceKey={state.sourceKey!}
                    onComplete={(result) => { updateState({ importResult: result }); setStep("summary"); }}
                />
            )}
            {step === "summary" && (
                <ImportSummaryStep
                    result={state.importResult!}
                    onNewImport={() => { setState(initialState); setStep("source"); }}
                />
            )}
        </div>
    );
}

ImportWizard.path = "/import-wizard";
